import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Filter,
  Plus,
  Edit,
  Users,
  Calendar,
  User,
  Trash2,
  AlertTriangle,
} from 'lucide-react';

import { Button } from '../components/ui/button.tsx';
import { Input } from '../components/ui/input.tsx';
import { Card, CardContent } from '../components/ui/card.tsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog.tsx';
import { Label } from '../components/ui/label.tsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select.tsx';
import { Badge } from '../components/ui/badge.tsx';
import { LoadingSpinner } from '../components/ui/loading-spinner.tsx';

import {
  useCreateClass,
  useGetAllClasses,
  useUpdateClass,
} from '../api/class/classQueries.js';
import { useGetAllSessions } from '../api/session/sessionQueries.js';
import { useGetAllStaff } from '../api/staff/staffQueries.js';

export default function ClassManagement() {
  const navigate = useNavigate();
  const { data: classes } = useGetAllClasses();

  const [searchTerm, setSearchTerm] = useState('');
  const [sessionFilter, setSessionFilter] = useState('all');
  const [staffFilter, setStaffFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [newClass, setNewClass] = useState({
    className: '',
    sessionId: '',
    formStaffId: '',
  });

  const {
    data: sessions,
    isLoading: isSessionLoading,
    isError: isSessionError,
  } = useGetAllSessions();

  const {
    data: staff,
    isLoading: isStaffLoading,
    isError: isStaffError,
  } = useGetAllStaff();

  const { mutate: createClass } = useCreateClass();
  const { mutate: updateClass } = useUpdateClass();

  const itemsPerPage = 6;

  // Filter and search logic
  const filteredClasses = classes.data.filter((cls) => {
    const matchesSearch =
      cls.className.toLowerCase().includes(searchTerm.toLowerCase()) ||
      `${cls.formStaff.firstName} ${cls.formStaff.lastName}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
    const matchesSession =
      sessionFilter === 'all' || cls.sessionId === sessionFilter;
    const matchesStaff =
      staffFilter === 'all' || cls.formStaffId === staffFilter;

    return matchesSearch && matchesSession && matchesStaff;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredClasses.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedClasses = filteredClasses.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  const handleCreateClass = () => {
    if (newClass.className && newClass.sessionId && newClass.formStaffId) {
      createClass(newClass);
      setNewClass({ className: '', sessionId: '', formStaffId: '' });
      setIsCreateModalOpen(false);
    }
  };

  const handleEditClass = () => {
    if (
      selectedClass &&
      selectedClass.className &&
      selectedClass.sessionId &&
      selectedClass.formStaffId
    ) {
      const updatedClasses = {
        className: selectedClass.className,
        sessionId: selectedClass.sessionId,
        formStaffId: selectedClass.formStaffId,
      };
      updateClass({ classId: selectedClass.classId, data: updatedClasses });
      setIsEditModalOpen(false);
      setSelectedClass(null);
    }
  };

  const handleClassClick = (classId) => {
    navigate('/classes/' + classId);
  };

  return (
    <div className="space-y-6 p-6">
      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex flex-col sm:flex-row gap-4 flex-1">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder="Search classes or staff..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Filters */}
          <div className="flex gap-2">
            <Select value={sessionFilter} onValueChange={setSessionFilter}>
              <SelectTrigger className="w-40">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Session" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sessions</SelectItem>

                {isSessionError ? (
                  <div>Error loading sessions</div>
                ) : (
                  <>
                    {isSessionLoading ? (
                      <LoadingSpinner />
                    ) : (
                      <>
                        {sessions.data.map((session) => (
                          <SelectItem
                            key={session.sessionId}
                            value={session.sessionId}
                          >
                            {session.name}
                          </SelectItem>
                        ))}
                      </>
                    )}
                  </>
                )}
              </SelectContent>
            </Select>

            <Select value={staffFilter} onValueChange={setStaffFilter}>
              <SelectTrigger className="w-40">
                <User className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Staff" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Staff</SelectItem>
                {isStaffError ? (
                  <div>Error loading staff</div>
                ) : (
                  <>
                    {isStaffLoading ? (
                      <LoadingSpinner />
                    ) : (
                      <>
                        {staff.map((staff) => (
                          <SelectItem key={staff.staffId} value={staff.staffId}>
                            {staff.firstName} {staff.lastName}
                          </SelectItem>
                        ))}
                      </>
                    )}
                  </>
                )}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Create Button */}
        <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
          <DialogTrigger asChild>
            <Button className="bg-purple-600 hover:bg-purple-700 text-white">
              <Plus className="h-4 w-4 mr-2" />
              Create New Class
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Create New Class</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="className">Class Name</Label>
                <Input
                  id="className"
                  value={newClass.className}
                  onChange={(e) =>
                    setNewClass({ ...newClass, className: e.target.value })
                  }
                  placeholder="Enter class name"
                />
              </div>
              <div>
                <Label htmlFor="session">Session</Label>
                <Select
                  value={newClass.sessionId}
                  onValueChange={(value) =>
                    setNewClass({ ...newClass, sessionId: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select session" />
                  </SelectTrigger>
                  <SelectContent>
                    {isSessionError ? (
                      <div>Error loading sessions</div>
                    ) : (
                      <>
                        {isSessionLoading ? (
                          <LoadingSpinner />
                        ) : (
                          <>
                            {sessions.data.map((session) => (
                              <SelectItem
                                key={session.sessionId}
                                value={session.sessionId}
                              >
                                {session.name}
                              </SelectItem>
                            ))}
                          </>
                        )}
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="staff">Form Staff</Label>
                <Select
                  value={newClass.formStaffId}
                  onValueChange={(value) =>
                    setNewClass({ ...newClass, formStaffId: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select staff member" />
                  </SelectTrigger>
                  <SelectContent>
                    {isStaffError ? (
                      <div>Error loading staff</div>
                    ) : (
                      <>
                        {isStaffLoading ? (
                          <LoadingSpinner />
                        ) : (
                          <>
                            {staff.map((staff) => (
                              <SelectItem
                                key={staff.staffId}
                                value={staff.staffId}
                              >
                                {staff.firstName} {staff.lastName}
                              </SelectItem>
                            ))}
                          </>
                        )}
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2 pt-4">
                <Button
                  onClick={handleCreateClass}
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                >
                  Create Class
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setIsCreateModalOpen(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Class List */}
      <div className="space-y-4">
        {paginatedClasses.map((cls) => {
          const startDate = new Date(cls.session.startDate);
          const endDate = new Date(cls.session.endDate);
          const formattedStartDate = startDate.toLocaleDateString();
          const formattedEndDate = endDate.toLocaleDateString();
          return (
            <Card
              key={cls.classId}
              className="hover:shadow-lg transition-shadow cursor-pointer"
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div
                    className="flex-1"
                    onClick={() => handleClassClick(cls.classId)}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-lg font-semibold text-purple-600 hover:text-purple-700 mb-2">
                          {cls.className}
                        </h3>
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          <div className="flex items-center gap-1">
                            <User className="h-4 w-4" />
                            <span>
                              {cls.formStaff.firstName} {cls.formStaff.lastName}
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Users className="h-4 w-4" />
                            <span>
                              {cls.enrollments.length} student{' '}
                              {cls.enrollments.length === 1 ? ' ' : 's'}
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            <span>
                              {formattedStartDate} - {formattedEndDate}
                            </span>
                          </div>
                        </div>
                      </div>
                      <Badge variant="secondary" className="text-xs">
                        {cls.session.sessionName}
                      </Badge>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedClass(cls);
                      setIsEditModalOpen(true);
                    }}
                    className="text-purple-600 hover:text-purple-700 hover:bg-purple-50 ml-4"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedClass(cls);
                      setIsDeleteModalOpen(!isDeleteModalOpen);
                    }}
                    className="text-purple-600 hover:text-purple-700 hover:bg-purple-50 ml-4"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </Button>
          <div className="flex gap-1">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <Button
                key={page}
                variant={currentPage === page ? 'default' : 'outline'}
                onClick={() => setCurrentPage(page)}
                className={
                  currentPage === page
                    ? 'bg-purple-600 hover:bg-purple-700 text-white'
                    : ''
                }
              >
                {page}
              </Button>
            ))}
          </div>
          <Button
            variant="outline"
            onClick={() =>
              setCurrentPage(Math.min(totalPages, currentPage + 1))
            }
            disabled={currentPage === totalPages}
          >
            Next
          </Button>
        </div>
      )}

      {/* Edit Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Class</DialogTitle>
          </DialogHeader>
          {selectedClass && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="editClassName">Class Name</Label>
                <Input
                  id="editClassName"
                  value={selectedClass.className}
                  onChange={(e) =>
                    setSelectedClass({
                      ...selectedClass,
                      className: e.target.value,
                    })
                  }
                />
              </div>
              <div>
                <Label htmlFor="editSession">Session</Label>
                <Select
                  value={selectedClass.sessionId}
                  onValueChange={(value) =>
                    setSelectedClass({ ...selectedClass, sessionId: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {isSessionError ? (
                      <div>Error loading sessions</div>
                    ) : (
                      <>
                        {isSessionLoading ? (
                          <LoadingSpinner />
                        ) : (
                          <>
                            {sessions.data.map((session) => (
                              <SelectItem
                                key={session.sessionId}
                                value={session.sessionId}
                              >
                                {session.name}
                              </SelectItem>
                            ))}
                          </>
                        )}
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="editStaff">Form Staff</Label>
                <Select
                  value={selectedClass.formStaffId}
                  onValueChange={(value) =>
                    setSelectedClass({ ...selectedClass, formStaffId: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {isStaffError ? (
                      <div>Error loading staff</div>
                    ) : (
                      <>
                        {isStaffLoading ? (
                          <LoadingSpinner />
                        ) : (
                          <>
                            {staff.map((staff) => (
                              <SelectItem
                                key={staff.staffId}
                                value={staff.staffId}
                              >
                                {staff.firstName} {staff.lastName}
                              </SelectItem>
                            ))}
                          </>
                        )}
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2 pt-4">
                <Button
                  onClick={handleEditClass}
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                >
                  Save Changes
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setIsEditModalOpen(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Class */}
      <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Delete Class
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this class? You'll lose access to
              all data attached to this class.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-red-50 p-4 rounded-lg">
            <p className="text-sm text-red-800">
              <strong>What happens when you delete this:</strong>
            </p>
            <ul className="mt-2 text-sm text-red-700 space-y-1">
              <li>
                • You will lose all associated enrollments with this class
              </li>
              <li>• You will orphan student record entries in the database</li>
              <li>• Your data will not be preserved</li>
              <li>• You cannot undo this step</li>
            </ul>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDeleteModalOpen(false)}
            >
              Keep Class
            </Button>
            <Button
              onClick={() => {
                setIsDeleteModalOpen(false);
              }}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Yes, I am aware! Delete Class
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
